import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";

import { enrollSecretQuestions, getPresets } from "../api-service/secret-questions";
import { ROUTES } from "../common";
import messages from "../messages/ja.json";
import { AuthShell } from "../parts/auth-shell";
import { Loading } from "../parts/loading";
import { SelectMenu } from "../parts/select-menu";
import { useAuth } from "../provider";
import { SecretQuestionPreset } from "../types/secret-questions";

import "../styles/auth-flow.css";
import { Icon } from "icons";

interface AnswerRow {
  questionId: string;
  answer: string;
}

interface EnrollForm {
  rows: AnswerRow[];
}

const m = messages.secretQuestions;

// Minimal {placeholder} interpolation for the numbered question labels below.
const format = (template: string, values: Record<string, number | string>) =>
  template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ""));

// "必須" / "任意" chip on a slot header. Only the first `required_count` slots are required;
// the slots beyond that (up to `max_count`) are optional extras.
const SlotBadge = ({ required }: { required: boolean }) => (
  <span className={`secret-slot-badge ${required ? "is-required" : "is-optional"}`}>
    {required ? m.badgeRequired : m.badgeOptional}
  </span>
);

// Info strip above the slots (blue ⓘ + the page description).
const InfoIcon = () => (
  <svg className="secret-note-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <circle cx="8" cy="8" r="8" fill="currentColor" />
    <rect x="7.1" y="6.6" width="1.8" height="5.4" rx="0.9" fill="#fff" />
    <circle cx="8" cy="4.4" r="1" fill="#fff" />
  </svg>
);

// The row index inside a yup array test, read off the validation path ("rows[2].answer").
const rowIndexOf = (path?: string) => Number(/\[(\d+)\]/.exec(path ?? "")?.[1] ?? -1);

// A row is required when its index falls inside `required_count`, which the page hands to the
// schema through the react-hook-form validation context.
const isRequiredRow = (ctx: yup.TestContext) =>
  rowIndexOf(ctx.path) < ((ctx.options.context as any)?.requiredCount ?? 0);

// Checked on submit (and on every change afterwards), per row:
//   required row  → both halves must be filled in;
//   optional row  → all-empty is fine, but a half-filled row is not: a question without an
//                   answer and an answer without a question each flag the missing half.
// How many answers are *enough* beyond that is the backend's rule (see onSubmit).
const schema = yup.object({
  rows: yup.array().of(
    yup.object({
      questionId: yup
        .string()
        .defined()
        .test("question", "", function (value) {
          if (value) return true;
          if (isRequiredRow(this)) return this.createError({ message: m.questionRequired });
          if (this.parent?.answer?.trim()) return this.createError({ message: m.questionMissing });
          return true;
        }),
      answer: yup
        .string()
        .trim()
        .test("answer", "", function (value) {
          if (value) return true;
          if (isRequiredRow(this)) return this.createError({ message: m.answerRequired });
          if (this.parent?.questionId) return this.createError({ message: m.answerMissing });
          return true;
        }),
    })
  ),
});

// FE-1: secret-question enrollment. Reached via the RequireSecretQuestionsRegistered gate
// after face registration; on success the gate lets the flow continue to /verify-mfa.
export function SecretQuestionsEnrollPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [presets, setPresets] = useState<SecretQuestionPreset[] | null>(null);
  const [requiredCount, setRequiredCount] = useState(1);
  const [presetError, setPresetError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    clearErrors,
    trigger,
    watch,
    formState: { errors },
  } = useForm<EnrollForm>({
    resolver: yupResolver(schema) as any,
    defaultValues: { rows: [] },
    // Read back by the schema to tell required slots from optional ones.
    context: { requiredCount },
  });
  const { fields } = useFieldArray({ control, name: "rows" });

  const loadPresets = () => {
    setPresetError("");
    setPresets(null);
    getPresets()
      .then((data) => {
        const required = Math.max(data.required_count, 1);
        setPresets(data.questions);
        // One slot per answer the BE accepts (`max_count`), never more than there are questions
        // to pick from. The first `required_count` slots are marked and validated as required;
        // the rest are optional extras the user may leave empty.
        const max = Math.max(data.max_count || required, required);
        const slots = Math.min(max, data.questions.length);
        setRequiredCount(Math.min(required, slots));
        reset({
          rows: Array.from({ length: slots }, () => ({
            questionId: "",
            answer: "",
          })),
        });
      })
      .catch((err: any) => setPresetError(err?.error?.message || err?.message || m.presetError));
  };

  useEffect(() => {
    // A user who already enrolled must not hit the enroll API (BE 409). The render below
    // redirects them, so skip the presets fetch too.
    if (auth.secretStatus !== "registered") loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watchedRows = watch("rows");
  const selectedIds = (watchedRows ?? []).map((row) => row?.questionId).filter(Boolean);
  // Hide questions already chosen in other slots so each question is picked at most once.
  const optionsFor = (index: number) =>
    (presets ?? []).filter(
      (q) =>
        q.question_id === watchedRows?.[index]?.questionId || !selectedIds.includes(q.question_id)
    );

  // Every slot is numbered in its header ("秘密の質問3").
  const questionLabel = (index: number) => format(m.questionIndexed, { index: index + 1 });

  // "内容をリセット": clear both halves of a slot so a user who picked a question by mistake
  // can back out of it. A selected question otherwise can't be un-picked (the menu has no
  // empty option), which then forces an answer on submit.
  const resetSlot = (index: number, fieldId: string) => {
    setValue(`rows.${index}.questionId`, "", { shouldDirty: true });
    setValue(`rows.${index}.answer`, "", { shouldDirty: true });
    // Drop any errors the slot showed; an empty optional row is valid, so re-validate to keep
    // the two halves in step (clearErrors alone can be re-added by a pending revalidation).
    clearErrors([`rows.${index}.questionId`, `rows.${index}.answer`]);
    trigger([`rows.${index}.questionId`, `rows.${index}.answer`]);
    // The button disables itself once the slot is empty, so move focus to the slot's question
    // menu instead of dropping it to <body> (keyboard users).
    document.querySelector<HTMLElement>(`[aria-labelledby="${fieldId}-label"]`)?.focus();
  };

  // Same generic toast as a failed enroll: the per-field messages say what is wrong, this only
  // has to make sure the user notices something failed — the offending slot can be off-screen.
  const onInvalid = () => {
    toast.error(m.enrollError, { icon: <Icon name="warning" size={27} /> });
  };

  const onSubmit = async (data: EnrollForm) => {
    const answers = (data.rows ?? [])
      .filter((row) => row.questionId && row.answer.trim())
      .map((row) => ({ question_id: row.questionId, answer: row.answer.trim() }));

    setFormError("");
    // Nothing filled in at all → say so locally instead of round-tripping an empty POST.
    if (answers.length === 0) {
      setFormError(m.selectRequired);
      return;
    }
    // Beyond that, how many answers are enough (required_count) is the backend's rule and is
    // not re-checked here: what the user filled in is POSTed and the BE's error message is
    // shown as-is. That keeps FE and BE from disagreeing when the rule changes.
    setSubmitting(true);
    try {
      await enrollSecretQuestions(answers);
      auth.setSecretStatus("registered");
      toast.success(m.enrollSuccess);
      // Continue the onboarding flow; the gate now passes to /verify-mfa.
      navigate(ROUTES.checkin, { replace: true });
    } catch (err: any) {
      const msg = err?.error?.message || err?.message || m.enrollError;
      setFormError(msg);
      toast.error(msg, { icon: <Icon name="warning" size={27} /> });
    } finally {
      setSubmitting(false);
    }
  };

  // Already enrolled → nothing to do here; go to verify-mfa (avoids a BE 409 already_enrolled).
  if (auth.secretStatus === "registered") {
    return <Navigate to={ROUTES.checkin} replace />;
  }

  return (
    <AuthShell title={m.title} wide>
      {presetError ? (
        // Nothing to fill in: show just the reason and the retry, no intro copy and no
        // empty card padding underneath.
        <div className="secret-status">
          <p className="error-banner">{presetError}</p>
          <button type="button" className="primary-button button-full" onClick={loadPresets}>
            {m.statusRetry}
          </button>
        </div>
      ) : !presets ? (
        <Loading />
      ) : (
        <>
          <p className="secret-note">
            <InfoIcon />
            <span>{m.description}</span>
          </p>

          <form className="auth-form secret-form" onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
            {fields.map((field, index) => {
              const required = index < requiredCount;
              return (
                <div
                  key={field.id}
                  className={`secret-slot ${required ? "is-required" : "is-optional"}`}
                >
                  <div className="secret-slot-head">
                    <SlotBadge required={required} />
                    <span className="secret-slot-title">{questionLabel(index)}</span>
                    {/* Optional slots only: a required question always has to be answered, so
                        there is nothing to back out of. */}
                    {!required ? (
                      <button
                        type="button"
                        className="secret-reset-button"
                        onClick={() => resetSlot(index, field.id)}
                        disabled={
                          submitting ||
                          !(watchedRows?.[index]?.questionId || watchedRows?.[index]?.answer)
                        }
                      >
                        {m.resetSlot}
                      </button>
                    ) : null}
                  </div>

                  <div className="field">
                    <span id={`${field.id}-label`}>{m.selectLabel}</span>
                    <Controller
                      control={control}
                      name={`rows.${index}.questionId`}
                      render={({ field: control }) => (
                        <SelectMenu
                          name={control.name}
                          labelId={`${field.id}-label`}
                          value={control.value}
                          onChange={control.onChange}
                          onBlur={control.onBlur}
                          options={optionsFor(index).map((q) => ({
                            value: q.question_id,
                            label: q.text,
                          }))}
                          placeholder={m.selectPlaceholder}
                          disabled={submitting}
                          required={required}
                          invalid={!!errors.rows?.[index]?.questionId}
                          // Points at the message below so a screen reader reads the reason,
                          // not just "invalid".
                          describedBy={
                            errors.rows?.[index]?.questionId
                              ? `${field.id}-question-error`
                              : undefined
                          }
                        />
                      )}
                    />
                    {errors.rows?.[index]?.questionId ? (
                      <p className="field-error" id={`${field.id}-question-error`}>
                        {errors.rows[index]?.questionId?.message}
                      </p>
                    ) : null}
                  </div>

                  <label className="field">
                    <span>{m.answerLabel}</span>
                    <input
                      autoComplete="off"
                      disabled={submitting}
                      placeholder={m.answerPlaceholder}
                      aria-required={required || undefined}
                      aria-invalid={!!errors.rows?.[index]?.answer}
                      aria-describedby={
                        errors.rows?.[index]?.answer ? `${field.id}-answer-error` : undefined
                      }
                      {...register(`rows.${index}.answer`)}
                    />
                    {errors.rows?.[index]?.answer ? (
                      <p className="field-error" id={`${field.id}-answer-error`}>
                        {errors.rows[index]?.answer?.message}
                      </p>
                    ) : null}
                  </label>
                </div>
              );
            })}

            {formError ? <p className="error-banner">{formError}</p> : null}

            {/* Plain grid item on desktop (display: contents); a sticky bar on phones. */}
            <div className="secret-submit-bar">
              <button className="primary-button button-full" type="submit" disabled={submitting}>
                {m.register}
              </button>
            </div>
          </form>
        </>
      )}
    </AuthShell>
  );
}
